import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FaFileMedicalAlt,
  FaTimes,
  FaSave,
  FaRecycle,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Cookies from "js-cookie";
import Logo from "../../images/logopdf.png";
import WatermarkLogo from "../../assets/afrafep.png";
import api from "../../config/configApi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jsPDF } from "jspdf";

interface Professional {
  idProfissional: number;
  nmProfissional: string;
  nomePaciente: string;
  cpf: string;
  crm: string;
  especialidade: string;
}

interface ReceituarioLaudo {
  id: number;
  tipo: string;
  dtConsulta: string;
  hrConsulta: string;
  cid: string;
  atestadoQtdDias: number;
  prescricao: string;
  criadoEm: string;
  editadoEm: string;
  profissional: {
    idProfissional: number;
    nmProfissional: string;
    CRM?: string;
    especialidade: {
      idEspecialidade?: number;
      dsEspecialidade: string;
      exibeQuestEpidem?: string;
      realizaProced?: string;
    };
  };
  paciente: {
    cdPaciente: number;
    nrCarteiraPlano: string;
    nmPaciente: string;
    dtNascimento: string;
    flSexo: string;
    cdStatus: number;
    nuCpf: string;
    nmEndereco: string;
    nuEndereco: number;
    nmComplEndereco: string;
    nmBairroEndereco: string;
    nmCidadeEndereco: string;
    nmUfEndereco: string;
    cdCepEndereco: string;
    cartaoSus: string;
  };
}

interface ReceituarioProps {
  onClose?: () => void;
}

const ReceituarioMedico: React.FC<ReceituarioProps> = ({ onClose }) => {
  const [content, setContent] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receituariosExistentes, setReceituariosExistentes] = useState<ReceituarioLaudo[]>([]);
  const [showReceituariosList, setShowReceituariosList] = useState(false);
  const [loadingReceituarios, setLoadingReceituarios] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<ReactQuill>(null);

  const professional: Professional = {
    idProfissional: Number(Cookies.get("idProfissional")),
    nmProfissional: Cookies.get("nmProfissional") || "Médico não identificado",
    nomePaciente: Cookies.get("Nome") || "Paciente não identificado",
    cpf: Cookies.get("NuCPF") || "CPF não identificado",
    crm: Cookies.get("CRM") || "CRM não informado",
    especialidade:
      Cookies.get("dsEspecialidade") || "Especialidade não informada",
  };

  // Função para buscar receituários existentes do paciente
  const fetchReceituariosExistentes = async () => {
    setLoadingReceituarios(true);
    try {
      const token = sessionStorage.getItem("token");
      const cpf = professional.cpf;

      if (token && cpf) {
        const response = await api.get<ReceituarioLaudo[]>(
          `/sisclinic/receituarios/cpf/${cpf}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Filtrar apenas receituários (tipo "RECEITA") e ordenar por data mais recente
        const receituarios = response.data
          .filter(item => item.tipo === "RECEITA")
          .sort((a, b) => {
            const dateA = new Date(`${a.dtConsulta}T${a.hrConsulta}`);
            const dateB = new Date(`${b.dtConsulta}T${b.hrConsulta}`);
            return dateB.getTime() - dateA.getTime();
          });

        setReceituariosExistentes(receituarios);
      }
    } catch (error) {
      console.error("Erro ao buscar receituários existentes:", error);
      toast.error("Erro ao carregar receituários anteriores");
    } finally {
      setLoadingReceituarios(false);
    }
  };

  // Função para carregar um receituário existente no editor
  const carregarReceituarioExistente = (receituario: ReceituarioLaudo) => {
    setContent(receituario.prescricao);
    setShowReceituariosList(false);
    toast.success("Receituário carregado com sucesso!");
  };

  
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setShowEditor(false);
    setContent("");
    setShowReceituariosList(false);
    onClose?.();
  }, [onClose]);

  // Fechar modal ao pressionar ESC
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isModalOpen, handleClose]);

  const handleGenerateReceituario = () => {
    setIsModalOpen(true);
    setTimeout(() => {
      setShowEditor(true);
    }, 50);
    
    // Buscar receituários existentes quando abrir o modal
    fetchReceituariosExistentes();
  };


  // Função para formatar data CORRIGIDA - sem problemas de fuso horário
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // Divide a data no formato YYYY-MM-DD
    const [year, month, day] = dateString.split('-');
    
    // Cria a data no formato DD/MM/YYYY sem problemas de fuso horário
    return `${day}/${month}/${year}`;
  };

// Função melhorada para converter HTML em texto formatado (1 linha por item)
const htmlToFormattedText = (html: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const processNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || "").replace(/\s+/g, " ");
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      switch (el.tagName.toLowerCase()) {
        case "p":
          // 1 quebra de linha somente
          return Array.from(el.childNodes).map(processNode).join("") + "\n";
        case "br":
          return "\n";
        case "div":
          return Array.from(el.childNodes).map(processNode).join("") + "\n";
        case "strong":
        case "b":
        case "em":
        case "i":
          return Array.from(el.childNodes).map(processNode).join("");
        case "ul":
          return Array.from(el.children)
            .map((li) => "• " + processNode(li) + "\n")
            .join("");
        case "ol":
          return Array.from(el.children)
            .map((li, i) => `${i + 1}. ${processNode(li)}\n`)
            .join("");
        case "li":
          return Array.from(el.childNodes).map(processNode).join("").trim();
        default:
          return Array.from(el.childNodes).map(processNode).join("");
      }
    }
    return "";
  };

  let result = Array.from(tempDiv.childNodes).map(processNode).join("");

  // Normaliza: remove linhas em branco múltiplas
  result = result
    .replace(/\r/g, "")
    .replace(/\n{3,}/g, "\n")   // nunca mais que 1 linha vazia
    .replace(/[ \t]+/g, " ")
    .trim();

  return result;
};

  // Função para obter texto de pré-visualização
  const obterTextoPreview = (): string => {
    return htmlToFormattedText(content) || "(Nenhum conteúdo digitado)";
  };

const handleGeneratePDF = async () => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });

      const marginLeft = 40;
      const marginRight = 40;
      const marginTop = 0;
      const pageWidth =
        doc.internal.pageSize.getWidth() - marginLeft - marginRight;
      const centerX = doc.internal.pageSize.getWidth() / 2;

      // Logo
      const logoImg = new Image();
      logoImg.src = Logo;

      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        setTimeout(resolve, 1000);
      });

      const logoWidth = 600;
      const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
      doc.addImage(
        logoImg,
        "PNG",
        centerX - logoWidth / 2,
        marginTop,
        logoWidth,
        logoHeight
      );

      // Cabeçalho
      const headerY = marginTop + logoHeight + 30;
      doc.setFontSize(15);
      doc.setTextColor(36, 99, 235);
      doc.text(
        "Associação dos Auditores Fiscais do Estado da Paraíba",
        centerX,
        headerY,
        { align: "center" }
      );

      // Título
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("RECEITUÁRIO COMUM", centerX, headerY + 30, { align: "center" });

      // Nome do paciente
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(
        `Paciente: ${professional.nomePaciente}`,
        marginLeft,
        headerY + 90
      );

      // Conteúdo
      const contentY = headerY + 120;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Prescrição:", marginLeft, contentY);

      // Usar a função para formatar o texto do PDF
      const contentText = htmlToFormattedText(content);

      doc.setFont("helvetica", "normal");
      const splitText = doc.splitTextToSize(contentText, pageWidth);

      const lineHeight = 15;
      const maxHeight = doc.internal.pageSize.getHeight() - 250;
      let currentY = contentY + 20;

      for (let i = 0; i < splitText.length; i++) {
        if (currentY > maxHeight) {
          doc.addPage();
          currentY = marginTop;
        }
        doc.text(splitText[i], marginLeft, currentY);
        currentY += lineHeight;
      }

      // Marca d'água
      try {
        const watermarkImg = new Image();
        watermarkImg.src = WatermarkLogo;

        await new Promise((resolve, reject) => {
          watermarkImg.onload = resolve;
          watermarkImg.onerror = reject;
          setTimeout(resolve, 1000);
        });

        const watermarkWidth = 300;
        const watermarkHeight =
          (watermarkImg.height * watermarkWidth) / watermarkImg.width;
        const centerY = doc.internal.pageSize.getHeight() / 2;

        const canvas = document.createElement("canvas");
        canvas.width = watermarkImg.width;
        canvas.height = watermarkImg.height;
        const ctx = canvas.getContext("2d");

        if (ctx) {
          ctx.globalAlpha = 0.1;
          ctx.drawImage(watermarkImg, 0, 0, canvas.width, canvas.height);
          doc.addImage(
            canvas,
            "PNG",
            centerX - watermarkWidth / 2,
            centerY - watermarkHeight / 2,
            watermarkWidth,
            watermarkHeight
          );
        } else {
          doc.addImage(
            watermarkImg,
            "PNG",
            centerX - watermarkWidth / 2,
            centerY - watermarkHeight / 2,
            watermarkWidth,
            watermarkHeight
          );
        }
      } catch (error) {
        console.warn("Erro ao carregar marca d'água:", error);
      }

      // Assinatura
      const signatureY = doc.internal.pageSize.getHeight() - 290;
      doc.text(
        "_________________________________________",
        centerX,
        signatureY,
        { align: "center" }
      );
      doc.setFont("helvetica", "bold");
      doc.text(`${professional.nmProfissional}`, centerX, signatureY + 20, {
        align: "center",
      });
      doc.setFont("helvetica", "normal");
      doc.text(`CRM: ${professional.crm}`, centerX, signatureY + 40, {
        align: "center",
      });
      doc.text(professional.especialidade, centerX, signatureY + 60, {
        align: "center",
      });

   // Data no lado direito
const dataAtual = new Date().toLocaleDateString('pt-BR');
const dataX = doc.internal.pageSize.getWidth() - marginRight;
doc.text("Data:  "  , dataX - 52, signatureY + 70, { align: "right" });
doc.text(dataAtual, dataX, signatureY + 70, { align: "right" });
      // Rodapé
      const footerY = doc.internal.pageSize.getHeight() - 80;
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const leftColX = marginLeft;

      doc.text(
        "Rua Corálio Soares de Oliveira, 497 • Centro, João Pessoa • PB",
        leftColX,
        footerY
      );
      doc.text("Fone: (83) 3533.5310 | 3533.5311", leftColX, footerY + 15);
      doc.text("www.afrafep.org.br", leftColX, footerY + 30);
      doc.text("INSC. ANS N° 330281", leftColX, footerY + 45);

      doc.setTextColor(0, 0, 255);
      const rightColX = marginLeft + pageWidth / 2 + 80;

      doc.text("Ética e Transparência", rightColX, footerY);
      doc.text(
        "Solidariedade e Responsabilidade Social",
        rightColX,
        footerY + 15
      );
      doc.text("Comprometimento e Profissionalismo", rightColX, footerY + 30);
      doc.text("Legalidade e Respeito", rightColX, footerY + 45);

      // Gerar PDF
      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);

      iframe.onload = function () {
        setTimeout(function () {
          iframe.contentWindow?.print();
        }, 500);
      };

      return pdfBlob;
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      throw error;
    }
  };

  const handleSaveReceituario = async () => {
    if (!content || content === "<p><br></p>" || !content.trim()) {
      toast.error("O campo de prescrição do receituário é obrigatório!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsSubmitting(true);

    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split("T")[0];
    const formattedTime = currentDate.toTimeString().substring(0, 5);

    const payload = {
      nuCpf: professional.cpf,
      idProfissional: professional.idProfissional,
      tipo: "RECEITA",
      dtConsulta: formattedDate,
      hrConsulta: formattedTime,
      prescricao: content,
    };

    try {
      const response = await api.post("/receituarios", payload);

      if (response.status === 200 || response.status === 201) {
        toast.success("Receituário salvo com sucesso!");

        setTimeout(async () => {
          try {
            await handleGeneratePDF();
            setTimeout(() => {
              handleClose();
              setIsSubmitting(false);
            }, 2000);
          } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            toast.error("Erro ao gerar PDF");
            setIsSubmitting(false);
          }
        }, 1500);
      } else {
        throw new Error(`Erro ao salvar receituário: ${response.status}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Ocorreu um erro ao salvar o receituário");
      setIsSubmitting(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
  ];

  return (
    <div className="space-y-4">
      <button
        onClick={handleGenerateReceituario}
        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200"
      >
        <FaFileMedicalAlt />
        Receituário Comum
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={handleClose}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
            ref={modalRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b p-4 bg-blue-50 rounded-t-xl">
              <h2 id="modal-title" className="text-xl font-bold text-gray-800">
                Receituário Comum
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
                aria-label="Fechar modal"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Lista de Receituários Existentes */}
              {showReceituariosList && (
                <div className="w-80 border-r bg-gray-50 overflow-y-auto">
                  <div className="p-4 border-b">
                    <h3 className="font-bold text-gray-700 mb-2">
                      Receituários Anteriores
                    </h3>
                    <button
                      onClick={() => setShowReceituariosList(false)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      ← Voltar ao editor
                    </button>
                  </div>
                  
                  {loadingReceituarios ? (
                    <div className="p-4 text-center">
                      <p className="text-gray-500">Carregando receituários...</p>
                    </div>
                  ) : receituariosExistentes.length > 0 ? (
                    <div className="p-2">
                      {receituariosExistentes.map((receituario) => (
                        <div
                          key={receituario.id}
                          className="p-3 mb-2 border rounded-lg bg-white hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => carregarReceituarioExistente(receituario)}
                        >
                          <div className="font-semibold text-sm text-gray-800">
                            {formatDate(receituario.dtConsulta)}
                          </div>
                          <div className="text-xs text-blue-600 mt-1 font-medium">
                            Dr. {receituario.profissional.nmProfissional}
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-1">
                            {receituario.prescricao.replace(/<[^>]*>/g, "").substring(0, 60)}...
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-gray-500">Nenhum receituário anterior encontrado</p>
                    </div>
                  )}
                </div>
              )}

              {/* Editor Principal */}
              <div className={`flex-1 overflow-y-auto flex flex-col ${showReceituariosList ? '' : 'w-full'}`}>
                <div className="p-6 overflow-y-auto flex-1 text-gray-900 leading-relaxed text-[15px]">
                  <div className="mb-8 text-center border-b pb-6">
                    <img
                      src={Logo}
                      alt="Logo Afrafep"
                      className="w-full max-w-md mx-auto mb-4 object-contain"
                    />
                    <div className="text-blue-600 font-bold">
                      <div className="text-sm font-normal leading-tight">
                        Associação dos Auditores Fiscais do Estado da Paraíba
                      </div>
                      <div className="text-2xl font-bold leading-tight mt-1">
                        RECEITUÁRIO COMUM
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-6 p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="mb-1">
                        <strong>Paciente:</strong> {professional.nomePaciente}
                      </p>
                      <p className="mb-1">
                        <strong>Médico:</strong> Dr. {professional.nmProfissional} - CRM: {professional.crm}
                      </p>
                      <p>
                        <strong>Especialidade:</strong> {professional.especialidade}
                      </p>
                    </div>
                    
                    {/* Botão Reutilizar Receituário - só aparece quando NÃO está mostrando a lista */}
                    {!showReceituariosList && receituariosExistentes.length > 0 && (
                      <button
                        onClick={() => setShowReceituariosList(true)}
                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded flex items-center gap-2 text-sm"
                      >
                        <FaRecycle />
                        Reutilizar Receituário
                      </button>
                    )}
                  </div>

                  <section className="mb-8">
                    <h3 className="font-bold mb-2 text-blue-700">Prescrição:</h3>

                    <div className="space-y-4">
                      <div className="border rounded-lg overflow-hidden">
                        {showEditor && (
                          <ReactQuill
                            ref={editorRef}
                            value={content}
                            onChange={setContent}
                            modules={modules}
                            formats={formats}
                            placeholder="Digite a prescrição médica aqui..."
                            style={{
                              minHeight: "200px",
                              marginBottom: "50px",
                            }}
                            theme="snow"
                          />
                        )}
                      </div>
                    </div>

                    {/* Pré-visualização */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <h4 className="font-bold text-blue-700 mb-2">
                        Visualizador:
                      </h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {obterTextoPreview()}
                      </div>
                    </div>

                    <div className="mt-2 text-sm text-gray-500">
                      Use o editor acima para escrever a prescrição médica em formato livre.
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <div className="border-t p-4 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowReceituariosList(!showReceituariosList)}
                className="bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200"
              >
                <FaRecycle />
                {showReceituariosList ? "Voltar ao Editor" : "Ver Receituários Anteriores"}
              </button>         
              <button
                onClick={handleSaveReceituario}
                disabled={isSubmitting}
                className={`bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded flex items-center gap-2 transition-colors duration-200 ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {isSubmitting ? "Enviando..." : "Imprimir Receituário"}
              </button>
            </div>

            <ToastContainer
              position="top-right"
              autoClose={3500}
              hideProgressBar={false}
              toastClassName={() =>
                "bg-black text-white font-semibold rounded shadow-md p-3"
              }
              progressClassName="bg-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceituarioMedico;